import { Box, Tooltip, Typography } from "@mui/material";
import type { ReactElement } from "react";
import { CONDENSED_FONT_FAMILY } from "./constants";
import { PICK_TYPE_DEFINITIONS, pickTypeLabel } from "./pickTypes";

/*
 * Half-court sketches of each PickType's location and spacing, for the hover
 * on the PR / Sm-Sm Type tables' row labels (Jacob, 2026-10-07; Trevor's review
 * 2026-10-20). Hand-authored from the type definitions (pickTypes.ts) in the
 * shot chart's frame: feet, baseline at the top, rim at (25, 5.25). Side
 * screens are drawn on the right.
 */

type Pt = [number, number];

interface Play {
  handler: Pt;
  screeners: Pt[];
  /** The handler's path: start, bend, end (a quadratic curve) */
  path: [Pt, Pt, Pt];
  spacers: Pt[];
  /** The spot whose emptiness defines the type */
  empty?: Pt;
  /** Quick Ghost: the screener's approach and pop, and no screen set */
  ghost?: { approach: [Pt, Pt]; pop: [Pt, Pt, Pt] };
  /** A step-up screen: the screener's path up from the baseline to the screen */
  stepUp?: [Pt, Pt, Pt];
  /**
   * The screen's angle in degrees from the baseline (0: parallel to the baseline, 90: parallel to the sideline). The
   * handler goes past it at a right angle, so it sets where the screen sends him (Trevor, 2026-10-20). Default:
   * square to the line from screener to handler.
   */
  screenAngle?: number;
}

const LC: Pt = [2.5, 3.5];
const RC: Pt = [47.5, 3.5];
const LW: Pt = [6.5, 21.5];
const RW: Pt = [43.5, 21.5];
const LOW_L: Pt = [14.5, 4]; // the dunker spot, just outside the paint
const TOP: Pt = [25, 32.5];

const MIDDLE_PICK = {
  handler: [25, 34] as Pt,
  screeners: [[27.6, 30.8]] as Pt[],
  path: [[25.6, 34.2], [33.5, 34], [34, 24.5]] as [Pt, Pt, Pt],
};
const SIDE_MIDDLE = {
  handler: [41.5, 29] as Pt,
  screeners: [[37.2, 26.4]] as Pt[],
  path: [[41, 29.6], [35.5, 32], [28.5, 23.5]] as [Pt, Pt, Pt],
};
// The screener steps up from the baseline and sets the screen on the handler's baseline side, so the screen's angle
// sends the handler toward the baseline (Trevor, 2026-10-20).
const SIDE_STEP = {
  handler: [39, 32.4] as Pt,
  screeners: [[41.2, 27.6]] as Pt[],
  path: [[39.8, 32.8], [45.6, 32.6], [45.4, 17.5]] as [Pt, Pt, Pt],
  stepUp: [[34.5, 9.5], [38, 20.5], [40.8, 25.9]] as [Pt, Pt, Pt],
  screenAngle: 0,
};

const PLAYS: Record<string, Play> = {
  Middle: { ...MIDDLE_PICK, spacers: [LC, RC, LW] },
  Flat: { ...MIDDLE_PICK, spacers: [LC, RC, LOW_L] },
  // Set a little past parallel to the sideline, sending the handler middle and up the floor (Trevor, 2026-10-20)
  Corner: {
    handler: [44.5, 15.5],
    screeners: [[40.6, 14]],
    path: [[44, 16.2], [40.5, 18.6], [33, 21.5]],
    spacers: [LC, LW, TOP],
    empty: RC,
    screenAngle: 80,
  },
  "Angle Flip": { ...SIDE_MIDDLE, spacers: [LC, LW, RC] },
  Side: { ...SIDE_MIDDLE, spacers: [LC, LW, LOW_L], empty: RC },
  Angle: { ...SIDE_STEP, spacers: [LC, LW, RC] },
  Step: { ...SIDE_STEP, spacers: [LC, LW, LOW_L], empty: RC },
  "3-Man": {
    handler: [25, 34.2],
    screeners: [[20.6, 29], [29.4, 29]],
    path: [[25.6, 34.4], [33.5, 34.2], [34, 24.5]],
    spacers: [LC, RC],
  },
  "Quick Ghost": {
    handler: [25, 34],
    screeners: [[15.5, 28.6]],
    path: [[25.6, 34.2], [33.5, 34], [34, 24.5]],
    spacers: [LC, RC, LW],
    ghost: {
      approach: [[29.5, 22.5], [27, 30.2]],
      pop: [[27, 30.2], [21, 32.5], [16.8, 29.3]],
    },
  },
};

const K = 4.2; // px per foot
const DEPTH = 38; // feet of court shown from the baseline
const INK = "#111";
const SCREENER = "#77716b";
const SPACER = "#4c4c4c";
const LINE = "#cfccc8";
const EMPTY = "#9e9892";

const px = ([x, y]: Pt): Pt => [x * K, y * K];

function Arrowhead({ from, to, color }: { from: Pt; to: Pt; color: string }) {
  const [x0, y0] = px(from);
  const [x1, y1] = px(to);
  const len = Math.hypot(x1 - x0, y1 - y0) || 1;
  const ux = (x1 - x0) / len;
  const uy = (y1 - y0) / len;
  const L = 6.5;
  const W = 3.4;
  const bx = x1 - ux * L;
  const by = y1 - uy * L;
  const pts = [
    [x1, y1],
    [bx - uy * W, by + ux * W],
    [bx + uy * W, by - ux * W],
  ]
    .map((p) => p.map((v) => v.toFixed(1)).join(","))
    .join(" ");
  return <polygon points={pts} fill={color} />;
}

function Curve({
  path: [a, c, b],
  color,
  dashed,
}: {
  path: [Pt, Pt, Pt];
  color: string;
  dashed?: boolean;
}) {
  const [ax, ay] = px(a);
  const [cx, cy] = px(c);
  const [bx, by] = px(b);
  // Stop the stroke short of the tip so the head reads cleanly.
  const len = Math.hypot(bx - cx, by - cy) || 1;
  const ex = bx - ((bx - cx) / len) * 4;
  const ey = by - ((by - cy) / len) * 4;
  return (
    <g>
      <path
        d={`M ${ax} ${ay} Q ${cx} ${cy} ${ex} ${ey}`}
        fill="none"
        stroke={color}
        strokeWidth={1.6}
        strokeDasharray={dashed ? "3 2.2" : undefined}
      />
      <Arrowhead from={c} to={b} color={color} />
    </g>
  );
}

/** The screen: a bar on the handler's side of the screener, square to him or at the play's screenAngle. */
function ScreenBar({ screener, handler, angle, dashed }: { screener: Pt; handler: Pt; angle?: number; dashed?: boolean }) {
  const dx = handler[0] - screener[0];
  const dy = handler[1] - screener[1];
  const len = Math.hypot(dx, dy) || 1;
  let ux = dx / len;
  let uy = dy / len;
  if (angle != null) {
    // the bar's normal, turned toward the handler
    const r = (angle * Math.PI) / 180;
    const sign = -Math.sin(r) * dx + Math.cos(r) * dy >= 0 ? 1 : -1;
    ux = -Math.sin(r) * sign;
    uy = Math.cos(r) * sign;
  }
  const c: Pt = [screener[0] + ux * 1.7, screener[1] + uy * 1.7];
  const a = px([c[0] - uy * 1.9, c[1] + ux * 1.9]);
  const b = px([c[0] + uy * 1.9, c[1] - ux * 1.9]);
  return (
    <line
      x1={a[0]}
      y1={a[1]}
      x2={b[0]}
      y2={b[1]}
      stroke={INK}
      strokeWidth={2.4}
      strokeDasharray={dashed ? "2 1.6" : undefined}
    />
  );
}

function Court() {
  const line = { fill: "none", stroke: LINE, strokeWidth: 1 };
  const r3 = 23.75 * K;
  return (
    <g>
      <rect x={0} y={0} width={50 * K} height={DEPTH * K} fill="#fff" />
      <rect x={17 * K} y={0} width={16 * K} height={19 * K} {...line} />
      <path d={`M ${19 * K} ${19 * K} A ${6 * K} ${6 * K} 0 0 0 ${31 * K} ${19 * K}`} {...line} />
      <line x1={22 * K} y1={4 * K} x2={28 * K} y2={4 * K} stroke="#8a857f" strokeWidth={1.2} />
      <circle cx={25 * K} cy={5.25 * K} r={0.75 * K} fill="none" stroke="#8a857f" strokeWidth={1.1} />
      <line x1={3 * K} y1={0} x2={3 * K} y2={14 * K} {...line} />
      <line x1={47 * K} y1={0} x2={47 * K} y2={14 * K} {...line} />
      <path d={`M ${3 * K} ${14 * K} A ${r3} ${r3} 0 0 0 ${47 * K} ${14 * K}`} {...line} />
      <rect x={0.5} y={0.5} width={50 * K - 1} height={DEPTH * K - 1} fill="none" stroke="#4c4c4c" strokeWidth={1} />
    </g>
  );
}

/** One type's half-court sketch, or null for a value with no definition. */
export function PickTypeDiagram({ type }: { type: string }): ReactElement | null {
  const play = PLAYS[pickTypeLabel(type)];
  if (!play) return null;
  const dot = (p: Pt, r: number) => {
    const [x, y] = px(p);
    return { cx: x, cy: y, r: r * K };
  };
  return (
    <svg
      width={50 * K}
      height={DEPTH * K}
      viewBox={`0 0 ${50 * K} ${DEPTH * K}`}
      role="img"
      aria-label={`${pickTypeLabel(type)} pick diagram`}
      style={{ display: "block" }}
    >
      <Court />
      {play.empty && (
        <circle {...dot(play.empty, 1.3)} fill="none" stroke={EMPTY} strokeWidth={1.2} strokeDasharray="2.2 1.8" />
      )}
      {play.spacers.map((p, i) => (
        <circle key={i} {...dot(p, 1.25)} fill="#fff" stroke={SPACER} strokeWidth={1.3} />
      ))}
      {play.ghost && (
        <g>
          <Curve path={[play.ghost.approach[0], play.ghost.approach[0], play.ghost.approach[1]]} color={SCREENER} dashed />
          <Curve path={play.ghost.pop} color={SCREENER} dashed />
          <ScreenBar screener={play.ghost.approach[1]} handler={play.handler} dashed />
        </g>
      )}
      {play.stepUp && <Curve path={play.stepUp} color={SCREENER} dashed />}
      <Curve path={play.path} color={INK} />
      {play.screeners.map((s, i) => (
        <g key={i}>
          {!play.ghost && <ScreenBar screener={s} handler={play.handler} angle={play.screenAngle} />}
          <circle {...dot(s, 1.45)} fill={SCREENER} />
        </g>
      ))}
      <circle {...dot(play.handler, 1.55)} fill={INK} />
    </svg>
  );
}

const LEGEND_SX = {
  fontFamily: CONDENSED_FONT_FAMILY,
  fontSize: "0.69rem",
  color: "#6d6862",
  display: "flex",
  alignItems: "center",
  gap: "3px",
  whiteSpace: "nowrap" as const,
};

function Legend() {
  const glyph = (el: ReactElement) => (
    <svg width={9} height={9} viewBox="0 0 9 9" style={{ display: "block" }}>
      {el}
    </svg>
  );
  const items: [ReactElement, string][] = [
    [glyph(<circle cx={4.5} cy={4.5} r={3.6} fill={INK} />), "handler"],
    [glyph(<circle cx={4.5} cy={4.5} r={3.6} fill={SCREENER} />), "screener"],
    [glyph(<circle cx={4.5} cy={4.5} r={3.2} fill="#fff" stroke={SPACER} strokeWidth={1.2} />), "spacer"],
    [
      glyph(<circle cx={4.5} cy={4.5} r={3.2} fill="none" stroke={EMPTY} strokeWidth={1.1} strokeDasharray="1.8 1.4" />),
      "empty",
    ],
  ];
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
      {items.map(([g, label]) => (
        <Box key={label} sx={LEGEND_SX}>
          {g}
          {label}
        </Box>
      ))}
    </Box>
  );
}

/** Tooltip body: name, sketch, one-line definition, key. */
export function PickTypeCard({ type }: { type: string }) {
  const label = pickTypeLabel(type);
  return (
    <Box sx={{ width: 50 * K }}>
      <Typography
        sx={{
          fontFamily: CONDENSED_FONT_FAMILY,
          fontWeight: 700,
          fontSize: "0.82rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          color: INK,
          borderBottom: `2px solid ${INK}`,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <PickTypeDiagram type={type} />
      {PICK_TYPE_DEFINITIONS[label] && (
        <Typography
          sx={{ fontFamily: CONDENSED_FONT_FAMILY, fontSize: "0.76rem", lineHeight: 1.3, color: INK, mt: 0.5 }}
        >
          {PICK_TYPE_DEFINITIONS[label]}
        </Typography>
      )}
      <Legend />
    </Box>
  );
}

/**
 * A PickType row label: the display name, with the type's sketch on hover
 * (types without a sketch, e.g. the All row, render as plain text).
 */
export function PickTypeLabel({ value, children }: { value: string; children?: ReactElement }) {
  const label = pickTypeLabel(value);
  const content = children ?? <span>{label}</span>;
  if (!PLAYS[label]) return content;
  return (
    <Tooltip
      title={<PickTypeCard type={value} />}
      placement="right-start"
      enterDelay={150}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "#fff",
            color: INK,
            border: "1px solid #cfccc8",
            borderRadius: "3px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            p: 1,
            maxWidth: "none",
          },
        },
      }}
    >
      <Box component="span" sx={{ display: "inline-flex", cursor: "help" }}>
        {content}
      </Box>
    </Tooltip>
  );
}

export const PICK_TYPE_DIAGRAM_TYPES = Object.keys(PLAYS);
